import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '@/features/auth/AuthContext';
import { ApiError, fetchInvite, register } from '@/lib/api';

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const infoQuery = useQuery({
    queryKey: ['invite', token],
    queryFn: () => fetchInvite(token),
    retry: false,
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register({ email, full_name: fullName, password, invite_token: token });
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invite_invalid') setError('convite inválido ou expirado.');
        else if (err.code === 'conflict') setError('email já está cadastrado.');
        else if (err.code === 'validation_error') setError('dados inválidos. Senha precisa de 12+ chars, letra e dígito.');
        else setError(err.message);
      } else {
        setError('falha ao cadastrar — tente novamente.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (infoQuery.isLoading) return <div className="p-8 text-slate-500">verificando convite…</div>;
  if (infoQuery.isError || !infoQuery.data) {
    return <div className="p-8 text-red-600">convite inválido ou expirado.</div>;
  }

  const invite = infoQuery.data;

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Aceitar convite</h1>
        <p className="text-sm text-slate-600">
          <span className="font-medium">{invite.inviter_name}</span> convidou você para o household{' '}
          <span className="font-medium">{invite.household_name}</span> como {invite.role}.
        </p>
      </div>
      <form onSubmit={onSubmit} className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">Nome completo</span>
          <input
            required
            minLength={2}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">Email {invite.email && <em className="text-slate-400">(convite para {invite.email})</em>}</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">Senha (mín. 12, com letra e dígito)</span>
          <input
            type="password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-slate-900 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'cadastrando…' : 'Criar conta e entrar'}
        </button>
      </form>
    </main>
  );
}
